/**
 * Cipher layer for cloud-synced settings / contact roster / block list.
 *
 * HKDF-SHA256-derived sync key from a `cloudRootKey: Uint8Array (32B)` with
 * info `kaether:cloud-sync:enc:v1` and a per-itemKind salt label so a leaked
 * sync key for one kind cannot open another kind. AES-GCM-256 seals the
 * content. The manifest (covering `SHA256(blob)`, not the blob) is signed
 * via an injected Ed25519 signer closure so this file does not depend on
 * the global identity-key store.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/CloudSyncCipher.swift` (HKDF info +
 * AAD shape; the signer/verifier are an iOS protocol there, a closure here).
 *
 * THREAT MODEL
 *   - The KDS backend sees opaque AES-GCM ciphertext + the manifest. It
 *     never sees the cloud root key, the per-kind sync key, the plaintext,
 *     or the Ed25519 private key.
 *   - AES-GCM AAD binds each blob to its `itemKind`. A blob lifted from
 *     `settings` and dropped into `blockList` fails to open with an AAD
 *     mismatch — no partial decrypt.
 *   - The nonce is randomised per seal; two seals of the same plaintext
 *     produce different ciphertext, defeating same-content detection by
 *     the backend on per-version writes.
 *   - SHA-256(blob) is recomputed by `openItem` and compared against
 *     `manifest.blobSha256` BEFORE invoking AES-GCM-open. This catches
 *     blob substitution before we spend CPU on the AEAD, and gives a
 *     distinct error kind (`blobHashMismatch`) so audit logs can
 *     distinguish "wrong blob" from "wrong key".
 *   - The Ed25519 signature is verified BEFORE the SHA-256 check, before
 *     the AES-GCM-open — fail-closed at every layer.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs.
 *   Only SubtleCrypto (`crypto.subtle.*` + `crypto.getRandomValues`).
 *
 * @module CloudSyncCipher
 */

import {
  canonicalManifestBytes,
  type CloudSyncItemKind,
  type CloudSyncManifest,
} from "./CloudSyncRecord";

// ---------------------------------------------------------------------------
// Error kinds
// ---------------------------------------------------------------------------

export type CloudSyncCipherErrorKind =
  | "invalidRootKeyLength"
  | "invalidBlobLength"
  | "sealFailure"
  | "openFailure"
  | "blobHashMismatch"
  | "invalidSignature"
  | "kindMismatch";

/**
 * Structured error thrown by `sealItem` / `openItem`. The `kind` field is
 * the stable machine-readable discriminant; `detail` is a human-readable
 * hint that may include lengths, counts, etc.
 */
export class CloudSyncCipherError extends Error {
  readonly kind: CloudSyncCipherErrorKind;
  constructor(kind: CloudSyncCipherErrorKind, detail?: string) {
    super(detail ? `${kind}: ${detail}` : kind);
    this.name = "CloudSyncCipherError";
    this.kind = kind;
  }
}

// ---------------------------------------------------------------------------
// Signer / verifier DI types
// ---------------------------------------------------------------------------

/**
 * Ed25519 signer closure. Receives the canonical manifest bytes
 * (`canonicalManifestBytes(...)`) and returns a 64-byte signature.
 *
 * The caller — typically the identity-key store — provides this closure
 * so the cipher layer never holds the private key.
 */
export type ManifestSigner = (
  canonicalBytes: Uint8Array,
) => Promise<Uint8Array>;

/**
 * Ed25519 verifier closure. Receives the canonical manifest bytes and the
 * 64-byte signature and returns true iff the signature is valid under the
 * authoring device's public key.
 *
 * The caller is responsible for looking up the public key by
 * `manifest.authorDeviceId` (the protocol-level identity store).
 */
export type ManifestVerifier = (
  canonicalBytes: Uint8Array,
  signature: Uint8Array,
  authorDeviceId: number,
) => Promise<boolean>;

// ---------------------------------------------------------------------------
// Public result types
// ---------------------------------------------------------------------------

/**
 * Output of `sealItem`. The manifest is what gets uploaded to KDS alongside
 * the blob; the signature lives separate-but-paired with the manifest.
 */
export interface SealedCloudSyncItem {
  readonly manifest: CloudSyncManifest;
  readonly blob: Uint8Array;
  /** Ed25519 signature over `canonicalManifestBytes(manifest)` — 64 bytes. */
  readonly signature: Uint8Array;
}

// ---------------------------------------------------------------------------
// Internal: HKDF, AES-GCM, hashing primitives
// ---------------------------------------------------------------------------

const HKDF_INFO_PREFIX = "kaether:cloud-sync:enc:v1";

/** Per-itemKind salt label, distinguishes derived keys across kinds. */
function saltLabel(kind: CloudSyncItemKind): Uint8Array {
  return new TextEncoder().encode(`kaether:cloud-sync:salt:${kind}:v1`);
}

/** AAD label binding ciphertext to itemKind — fails open() on mismatch. */
function aadLabel(kind: CloudSyncItemKind): Uint8Array {
  return new TextEncoder().encode(`kaether:cloud-sync:aad:${kind}:v1`);
}

/** Random bytes via `crypto.getRandomValues`. */
function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

/**
 * SHA-256 hash. Used both for `canonical → signed bytes` (caller's signer
 * closure can choose whether to pre-hash) and for the `blobSha256` field.
 */
export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return new Uint8Array(buf);
}

/**
 * HKDF-SHA256 → AES-GCM-256 key import. The per-kind salt makes derived
 * keys non-portable across kinds.
 */
async function deriveSyncKey(
  rootKey: Uint8Array,
  kind: CloudSyncItemKind,
): Promise<CryptoKey> {
  if (rootKey.byteLength !== 32) {
    throw new CloudSyncCipherError(
      "invalidRootKeyLength",
      `cloudRootKey must be 32 bytes (got ${rootKey.byteLength})`,
    );
  }
  const baseKey = await crypto.subtle.importKey(
    "raw",
    rootKey,
    { name: "HKDF" },
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: saltLabel(kind),
      info: new TextEncoder().encode(HKDF_INFO_PREFIX),
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false /* not extractable */,
    ["encrypt", "decrypt"],
  );
}

/** AES-GCM seal returning combined `nonce(12) || ciphertext || tag(16)`. */
async function aesgcmSeal(
  plaintext: Uint8Array,
  key: CryptoKey,
  aad: Uint8Array,
): Promise<Uint8Array> {
  const nonce = randomBytes(12);
  let ctWithTag: ArrayBuffer;
  try {
    ctWithTag = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, additionalData: aad },
      key,
      plaintext,
    );
  } catch (e) {
    throw new CloudSyncCipherError(
      "sealFailure",
      e instanceof Error ? e.message : String(e),
    );
  }
  const out = new Uint8Array(12 + ctWithTag.byteLength);
  out.set(nonce, 0);
  out.set(new Uint8Array(ctWithTag), 12);
  return out;
}

/** AES-GCM open of combined `nonce(12) || ciphertext || tag(16)`. */
async function aesgcmOpen(
  combined: Uint8Array,
  key: CryptoKey,
  aad: Uint8Array,
): Promise<Uint8Array> {
  if (combined.byteLength < 28) {
    throw new CloudSyncCipherError(
      "invalidBlobLength",
      `blob must be at least 28 bytes (nonce 12 + tag 16); got ${combined.byteLength}`,
    );
  }
  const nonce = combined.subarray(0, 12);
  const ctWithTag = combined.subarray(12);
  let pt: ArrayBuffer;
  try {
    pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce, additionalData: aad },
      key,
      ctWithTag,
    );
  } catch (e) {
    throw new CloudSyncCipherError(
      "openFailure",
      e instanceof Error ? e.message : String(e),
    );
  }
  return new Uint8Array(pt);
}

/** Constant-time byte equality for fixed-length buffers. */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  let diff = 0;
  for (let i = 0; i < a.byteLength; i += 1) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Seal a content blob: derive the per-kind sync key, AES-GCM-seal under
 * an itemKind-bound AAD, compute SHA256(blob), assemble the manifest, and
 * Ed25519-sign the canonical manifest bytes via the injected signer.
 *
 * @param rootKey         32-byte cloud root key (derived from SVR-protected
 *                        seed once SVR ships).
 * @param kind            Item kind (settings, contactRoster, blockList).
 * @param plaintext       Bytes to seal.
 * @param manifestSigner  Ed25519 signer over `canonicalManifestBytes`.
 * @param meta            Caller-provided itemVersion / authorDeviceId / ts.
 *
 * @returns               `{ manifest, blob, signature }`. Caller uploads the
 *                        triple to KDS — see `CloudSyncStore.recordLocal`.
 *
 * @throws CloudSyncCipherError("invalidRootKeyLength") if root key isn't 32 B.
 * @throws CloudSyncCipherError("sealFailure") on AES-GCM failure.
 */
export async function sealItem(
  rootKey: Uint8Array,
  kind: CloudSyncItemKind,
  plaintext: Uint8Array,
  manifestSigner: ManifestSigner,
  meta: {
    readonly itemVersion: number;
    readonly authorDeviceId: number;
    readonly ts: number;
  },
): Promise<SealedCloudSyncItem> {
  const key = await deriveSyncKey(rootKey, kind);
  const blob = await aesgcmSeal(plaintext, key, aadLabel(kind));
  const blobSha256 = await sha256(blob);
  const manifest: CloudSyncManifest = {
    itemKind: kind,
    itemVersion: meta.itemVersion,
    blobSha256,
    authorDeviceId: meta.authorDeviceId,
    ts: meta.ts,
  };
  const canonical = canonicalManifestBytes(manifest);
  const signature = await manifestSigner(canonical);
  if (signature.byteLength !== 64) {
    throw new CloudSyncCipherError(
      "invalidSignature",
      `signer returned ${signature.byteLength} bytes; want 64`,
    );
  }
  return { manifest, blob, signature };
}

/**
 * Open a sealed item. Order of checks is strict — each layer fails closed
 * before spending work on the next:
 *
 *   1. itemKind in manifest matches the `kind` parameter (which controls
 *      the AAD and the derived key). Mismatch → `kindMismatch`.
 *   2. Ed25519 signature over `canonicalManifestBytes(manifest)` is valid
 *      under the authoring device's public key.
 *      → `invalidSignature` on failure.
 *   3. SHA-256(blob) equals manifest.blobSha256 (constant-time compare).
 *      → `blobHashMismatch` on failure.
 *   4. AES-GCM-open the blob under the per-kind sync key.
 *      → `openFailure` on tag/AAD failure.
 *
 * @param rootKey            32-byte cloud root key.
 * @param manifest           Manifest envelope.
 * @param blob               Sealed blob bytes.
 * @param signature          64-byte Ed25519 signature over canonical manifest.
 * @param manifestVerifier   Ed25519 verifier closure (looks up pubkey by
 *                           authorDeviceId).
 * @param expectedKind       The kind the caller is asking for. This is the
 *                           authoritative kind for AAD + key derivation; if
 *                           the manifest's kind differs, the call fails
 *                           with `kindMismatch` BEFORE any crypto runs.
 *
 * @returns                  Recovered plaintext bytes.
 *
 * @throws CloudSyncCipherError(...) per the matrix above.
 */
export async function openItem(
  rootKey: Uint8Array,
  manifest: CloudSyncManifest,
  blob: Uint8Array,
  signature: Uint8Array,
  manifestVerifier: ManifestVerifier,
  expectedKind: CloudSyncItemKind,
): Promise<Uint8Array> {
  // 1. Kind binding: the manifest must declare the same kind the caller is
  //    derive-keying for. If a hostile sync backend swapped a settings
  //    manifest for a blockList one, this rejects before crypto runs.
  if (manifest.itemKind !== expectedKind) {
    throw new CloudSyncCipherError(
      "kindMismatch",
      `manifest.itemKind=${manifest.itemKind} expected=${expectedKind}`,
    );
  }

  // 2. Ed25519 signature over canonical manifest bytes.
  const canonical = canonicalManifestBytes(manifest);
  const sigOk = await manifestVerifier(
    canonical,
    signature,
    manifest.authorDeviceId,
  );
  if (!sigOk) {
    throw new CloudSyncCipherError(
      "invalidSignature",
      `Ed25519 verify failed for authorDeviceId=${manifest.authorDeviceId}`,
    );
  }

  // 3. Blob hash matches the manifest's commitment.
  const actualHash = await sha256(blob);
  if (!timingSafeEqual(actualHash, manifest.blobSha256)) {
    throw new CloudSyncCipherError(
      "blobHashMismatch",
      "SHA-256(blob) does not match manifest.blobSha256",
    );
  }

  // 4. AES-GCM open. AAD binds to itemKind so cross-kind splices fail here
  //    (in addition to the explicit kind check in step 1, for layered
  //    defence in depth).
  const key = await deriveSyncKey(rootKey, expectedKind);
  return aesgcmOpen(blob, key, aadLabel(expectedKind));
}

/** Exposed for self-tests only — not part of the public API. */
export const __testing__ = {
  saltLabel,
  aadLabel,
  deriveSyncKey,
  sha256,
};
